// src/pages/userprofile/PreferencesPage.tsx
// UI preferences + privacy settings.

import { useState, useEffect } from 'react';
import { observer } from '@react';
import { useUserprofileSetting, useUserprofile, useI18n, useTheme, useAuth } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, ErrorAlert, Input } from '@react/components';
import { ToggleSwitch } from '../../components/ToggleSwitch';
import { ColorPicker } from '../../components/ColorPicker';
import { SegmentedControl } from '@/components/form';
import { accentColorOptions, themeOptions, densityOptions, profileVisibilityOptions, languageOptions, timezoneOptions, hexToHsl, accentForeground } from '../../features/userprofile/logic';
import { getBuiltinThemes, applyTheme as applyColorTheme, loadActiveThemeId } from '../../features/theme/logic';
import type { PrivacySettings, Userprofile } from '@core/types/userprofile';
import type { ThemeMode } from '@react/hooks';
import { Select } from '@/components/Select';

export const PreferencesPage = observer(() => {
  const settingStore = useUserprofileSetting();
  const profileStore = useUserprofile();
  const { t, setLocale } = useI18n();
  const { setMode: setThemeMode, setColors } = useTheme();
  const auth = useAuth();
  const profileId = (profileStore.currentItem as Userprofile | null)?.entity?.id ?? '';

  const [theme, setTheme] = useState('system');
  const [colorTheme, setColorTheme] = useState(() => loadActiveThemeId());
  const [accentColor, setAccentColor] = useState('#6366f1');
  const [density, setDensity] = useState('comfortable');
  const [dateFormat, setDateFormat] = useState('');
  const [timeFormat, setTimeFormat] = useState('');
  const [language, setLanguage] = useState('');
  const [timezone, setTimezone] = useState('');

  const [privacy, setPrivacy] = useState<PrivacySettings>({
    visibility: 'PROFILE_VISIBILITY_PUBLIC',
    show_in_search: true,
    show_in_directory: true,
    show_email: false,
    show_phone: false,
    show_location: true,
    show_online_status: true,
    show_date_of_birth: false,
    show_gender: false,
    show_last_active: true,
    show_activity_status: true,
    email_visible_to: [],
    phone_visible_to: [],
    custom_settings: {},
  });

  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!profileStore.currentItem && auth.userId) {
      profileStore.GetUserprofileDefault(auth.userId);
    }
  }, [auth.userId]);

  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;

    const loadSettings = async () => {
      try {
        const [uiResp, privacyResp] = await Promise.all([
          settingStore.GetUISettings(profileId),
          settingStore.GetPrivacySettings(profileId),
        ]);

        if (cancelled) return;

        if (uiResp.settings) {
          const s = uiResp.settings;
          const savedTheme = s.theme ?? 'system';
          const savedColorTheme = s.color_theme ?? loadActiveThemeId();
          const savedDensity = s.compact_mode === 'true' ? 'compact' : 'comfortable';
          const savedLanguage = s.language ?? '';

          setTheme(savedTheme);
          setColorTheme(savedColorTheme);
          setAccentColor(s.accent_color ?? '#6366f1');
          setDensity(savedDensity);
          setDateFormat(s.date_format ?? '');
          setTimeFormat(s.time_format ?? '');
          setLanguage(savedLanguage);
          setTimezone(s.timezone ?? '');

          // Apply loaded settings to UI
          setThemeMode(savedTheme as ThemeMode);
          applyColorTheme(savedColorTheme);
          if (savedDensity === 'compact') {
            document.documentElement.classList.add('compact');
          }
          if (savedLanguage) {
            setLocale(savedLanguage as any).catch(() => {});
          }
          if (s.accent_color) {
            const hsl = hexToHsl(s.accent_color);
            const fg = accentForeground(hsl);
            setColors({ light: { primary: hsl, primaryForeground: fg, ring: hsl }, dark: { primary: hsl, primaryForeground: fg, ring: hsl } });
          }
        }

        if (privacyResp.settings) {
          setPrivacy(privacyResp.settings);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) setSettingsLoaded(true);
      }
    };

    loadSettings();
    return () => { cancelled = true; };
  }, [profileId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveUI = async () => {
    setError(null);
    try {
      const settings = {
        theme,
        color_theme: colorTheme,
        accent_color: accentColor,
        compact_mode: density === 'compact' ? 'true' : 'false',
        sidebar_layout: density === 'compact' ? 'compact' : 'default',
        date_format: dateFormat,
        time_format: timeFormat,
        language,
        timezone,
      };

      await settingStore.SetUISettings(profileId, {
        profile: {} as never,
        settings,
      });

      // Update localStorage cache for instant restore on next page load
      try { localStorage.setItem('app_ui_settings', JSON.stringify(settings)); } catch { /* ignore */ }

      // Apply settings to UI immediately
      setThemeMode(theme as ThemeMode);
      applyColorTheme(colorTheme);
      if (language) {
        setLocale(language as any).catch(() => {});
      }
      if (density === 'compact') {
        document.documentElement.classList.add('compact');
      } else {
        document.documentElement.classList.remove('compact');
      }

      setSuccess(true);
    } catch (err) {
      setError(err);
    }
  };

  const handleResetUISettings = async () => {
    if (!profileId) return;
    setError(null);
    try {
      await settingStore.DeleteUISettings(profileId);
      try { localStorage.removeItem('app_ui_settings'); } catch { /* ignore */ }

      setTheme('system');
      setColorTheme(loadActiveThemeId());
      setAccentColor('#6366f1');
      setDensity('comfortable');
      setDateFormat('');
      setTimeFormat('');
      setLanguage('');
      setTimezone('');

      setThemeMode('system');
      applyColorTheme(loadActiveThemeId());
      document.documentElement.classList.remove('compact');

      setSuccess(true);
    } catch (err) {
      setError(err);
    }
  };

  const handleSavePrivacy = async () => {
    setError(null);
    try {
      await settingStore.SetPrivacySettings(profileId, { profile: {} as never, settings: privacy });
      setSuccess(true);
    } catch (err) {
      setError(err);
    }
  };

  const togglePrivacy = (field: keyof PrivacySettings) => {
    setPrivacy((prev) => ({ ...prev, [field]: !prev[field] } as PrivacySettings));
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('userprofile.preferencesTitle')}</h1>

      {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}
      {success && <Alert variant="success" dismissible onDismiss={() => setSuccess(false)}>{t('userprofile.saveSuccess')}</Alert>}

      {!profileId && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
      {profileId && !settingsLoaded && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}

      {/* UI Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>{t('userprofile.uiPreferences')}</CardTitle>
          <CardDescription>{t('userprofile.uiPreferencesDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Color Theme */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">{t('userprofile.colorTheme')}</p>
            <p className="text-xs text-muted-foreground mb-3">{t('userprofile.colorThemeDesc')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {getBuiltinThemes().map((def) => {
                const nameKey = `userprofile.themeName${def.id.charAt(0).toUpperCase()}${def.id.slice(1)}`;
                return (
                  <button
                    key={def.id}
                    onClick={() => {
                      setColorTheme(def.id);
                      applyColorTheme(def.id);
                    }}
                    className={`p-3 rounded-lg border text-center text-sm transition-colors ${
                      colorTheme === def.id ? 'border-primary bg-primary/5 font-medium ring-2 ring-primary/20' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex justify-center gap-1 mb-2">
                      <span className="w-4 h-4 rounded-full" style={{ background: `hsl(${def.preview.primary})` }} />
                      <span className="w-4 h-4 rounded-full" style={{ background: `hsl(${def.preview.accent})` }} />
                      <span className="w-4 h-4 rounded-full" style={{ background: `hsl(${def.preview.sidebar})` }} />
                    </div>
                    {t(nameKey)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Theme */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">{t('userprofile.colorScheme')}</p>
            <p className="text-xs text-muted-foreground mb-3">{t('userprofile.colorSchemeDesc')}</p>
            <SegmentedControl
              aria-label={t('userprofile.colorScheme')}
              value={theme}
              onChange={(value) => {
                setTheme(value);
                setThemeMode(value as ThemeMode);
                applyColorTheme(colorTheme);
              }}
              options={themeOptions.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))}
            />
          </div>

          {/* Accent color */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">{t('userprofile.accentColor')}</p>
            <ColorPicker options={accentColorOptions} value={accentColor} onChange={(hex) => {
              setAccentColor(hex);
              const hsl = hexToHsl(hex);
              const fg = accentForeground(hsl);
              setColors({ light: { primary: hsl, primaryForeground: fg, ring: hsl }, dark: { primary: hsl, primaryForeground: fg, ring: hsl } });
            }} />
          </div>

          {/* Density */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">{t('userprofile.layoutDensity')}</p>
            <SegmentedControl
              aria-label={t('userprofile.layoutDensity')}
              value={density}
              onChange={(value) => {
                setDensity(value);
                if (value === 'compact') {
                  document.documentElement.classList.add('compact');
                } else {
                  document.documentElement.classList.remove('compact');
                }
              }}
              options={densityOptions.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))}
            />
          </div>

          {/* Date/Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label={t('userprofile.dateFormat')} value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} placeholder="YYYY-MM-DD" />
            <Input label={t('userprofile.timeFormat')} value={timeFormat} onChange={(e) => setTimeFormat(e.target.value)} placeholder="HH:mm" />
          </div>

          {/* Language/Timezone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">{t('userprofile.language')}</label>
              <Select
                value={language}
                onChange={(e) => { setLanguage(e.target.value); if (e.target.value) setLocale(e.target.value as any).catch(() => {}); }} fullWidth={false}>
                <option value="">{t('userprofile.languageDefault')}</option>
                {languageOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">{t('userprofile.timezone')}</label>
              <Select value={timezone} onChange={(e) => setTimezone(e.target.value)} fullWidth={false}>
                <option value="">{t('userprofile.timezoneDefault')}</option>
                {timezoneOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </div>
          </div>
        </CardContent>
        <CardFooter className="justify-between">
          <Button variant="ghost" onClick={handleResetUISettings} disabled={!profileId}>{t('userprofile.resetToDefaults')}</Button>
          <Button onClick={handleSaveUI} isLoading={settingStore.isLoading} disabled={!profileId}>{t('common.saveChanges')}</Button>
        </CardFooter>
      </Card>

      {/* Privacy Settings */}
      <Card>
        <CardHeader>
          <CardTitle>{t('userprofile.privacySettings')}</CardTitle>
          <CardDescription>{t('userprofile.privacySettingsDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Visibility */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">{t('userprofile.profileVisibility')}</p>
            <Select
              value={privacy.visibility ?? ''}
              onChange={(e) => setPrivacy((p) => ({ ...p, visibility: e.target.value as PrivacySettings['visibility'] }))}>
              {profileVisibilityOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
              ))}
            </Select>
          </div>

          <ToggleSwitch label={t('userprofile.showInSearch')} description={t('userprofile.showInSearchDesc')} checked={privacy.show_in_search} onChange={() => togglePrivacy('show_in_search')} />
          <ToggleSwitch label={t('userprofile.showInDirectory')} description={t('userprofile.showInDirectoryDesc')} checked={privacy.show_in_directory} onChange={() => togglePrivacy('show_in_directory')} />
          <ToggleSwitch label={t('userprofile.showEmail')} checked={privacy.show_email} onChange={() => togglePrivacy('show_email')} />
          <ToggleSwitch label={t('userprofile.showPhone')} checked={privacy.show_phone} onChange={() => togglePrivacy('show_phone')} />
          <ToggleSwitch label={t('userprofile.showLocation')} checked={privacy.show_location} onChange={() => togglePrivacy('show_location')} />
          <ToggleSwitch label={t('userprofile.showOnlineStatus')} checked={privacy.show_online_status} onChange={() => togglePrivacy('show_online_status')} />
          <ToggleSwitch label={t('userprofile.showActivityStatus')} checked={privacy.show_activity_status} onChange={() => togglePrivacy('show_activity_status')} />
          <ToggleSwitch label={t('userprofile.showLastActive')} checked={privacy.show_last_active} onChange={() => togglePrivacy('show_last_active')} />
          <ToggleSwitch label={t('userprofile.showDateOfBirth')} checked={privacy.show_date_of_birth} onChange={() => togglePrivacy('show_date_of_birth')} />
          <ToggleSwitch label={t('userprofile.showGender')} checked={privacy.show_gender} onChange={() => togglePrivacy('show_gender')} />
        </CardContent>
        <CardFooter>
          <Button onClick={handleSavePrivacy} isLoading={settingStore.isLoading} disabled={!profileId}>{t('common.saveChanges')}</Button>
        </CardFooter>
      </Card>
    </div>
  );
});

export default PreferencesPage;
